/**
 * Static list of "motivos" (literary motifs) used in the Calderón Digital project.
 * Source: "Lista de motivos para Calderón Digital" by Fausta Antonucci (09/10/2021).
 *
 * Format: "CODE. Description" — mirrors how motivos appear in the `sinossi` field,
 * so the selected value can be used directly in an Elasticsearch match query.
 */
export const MOTIVOS: string[] = [
  // A. MITO, RELIGIÓN, SUPERSTICIONES, COSMOGONÍA
  'A1. Dioses del Panteón antiguo',
  'A01. Lucha o enemistad entre dioses/diosas',
  'A2. Dios cristiano',
  'A21. El Demonio como antagonista',
  'A22. Manifestaciones divinas / Milagros de Dios/de la Virgen',
  'A23. Dioses de otras religiones como antagonistas',
  'A3. Santos',
  'A31. Fe y constancia',
  'A32. Milagros',
  'A4. Supersticiones populares',
  'A5. Cataclismos naturales',
  'A6. Representaciones del más allá',
  'A61. Infierno',
  'A62. Paraíso',
  'A7. Monstruos o supuestos monstruos',
  'A8. Animales al servicio de una divinidad / animales protectores',
  'A9. Magias y hechizos',
  'A10. Parto en ambiente salvaje',
  'A11. Niño abandonado / perdido en la naturaleza',
  'A12. Lactancia animal',
  'A13. Objetos mágicos',
  'A14. Fantasmas, apariciones, visiones sobrenaturales',
  'A15. Propagación y defensa de la fe',
  'A16. Maldiciones y conjuros',

  // B. AMOR
  'B1. Amor correspondido',
  'B2. Amor no correspondido',
  'B3. Amor a primera vista',
  'B4. Celos',
  'B41. Celos motivados',
  'B42. Celos nacidos de equívocos',
  'B43. Celos retrospectivos',
  'B5. Amor obstaculizado',
  'B51. Amor obstaculizado por oposición de la-s familia-s',
  'B52. Amor obstaculizado por rivalidad y/o enemistades familiares y/o políticas',
  'B53. Amor obstaculizado por la rivalidad de un poderoso',
  'B54. Amor obstaculizado por la rivalidad de otro hombre / otra mujer',
  'B55. Amor obstaculizado por las leyes del honor y del matrimonio',
  'B56. Amor obstaculizado por diferencias sociales o económicas',
  'B6. Prendas y promesas',
  'B7. Citas nocturnas con sus eventuales equívocos',
  'B8. Rivalidad amorosa',
  'B9. El retrato',
  'B91. Retrato motivo de celos',
  'B92. Retrato causa de enamoramiento',
  'B93. Retrato verbal',
  'B10. Rechazo del amor',
  'B11. Galanteos',
  'B12. Amor fingido',
  'B13. Estrategias amorosas',
  'B14. Personaje al que se le pide que desempeñe un papel de medianero/a',

  // C. AMISTAD
  'C1. Amistad en conflicto con el amor',
  'C2. Amistad en conflicto con los deberes de vasallo',
  'C3. Amistad heroica',
  'C4. Complicidad entre amigos/amigas',

  // D. RELACIONES DE PODER
  'D1. El privado y su señor',
  'D2. El carcelero y el prisionero',
  'D3. Jerarquías militares',
  'D4. Amo y criado',
  'D41. Amo y criada',
  'D5. Señora y criada',
  'D51. Señora y criado',
  'D6. Jerarquías cortesanas',
  'D61. Señora y secretario',
  'D7. Reyes y reinas y sus súbditos',
  'D8. El señor y su vasallo',
  'D9. El gracioso ante los representantes del poder',
  'D10. El criado o criada que traiciona a su señor/a',
  'D11. El criado o criada que ayuda a su señor/a',
  'D12. La embajada ante un poderoso',
  'D13. Poder femenino vs. poder masculino',

  // E. RELACIONES FAMILIARES
  'E1. Relaciones paterno/materno-filiales',
  'E2. Relaciones entre hermanos y/o hermanas',
  'E3. Relaciones entre marido y mujer',
  'E31. Amor conyugal',
  'E32. Enemistad y riñas conyugales',
  'E33. Celos conyugales patológicos',
  'E34. Celos conyugales motivados',
  'E4. Conflicto entre amor paterno/materno y deberes de vasallo',

  // F. ENGAÑOS Y DISFRACES
  'F1. Cambio de identidad',
  'F2. Cambio de identidad que implica inversión de roles sociales (amo-criado)',
  'F3. Disfraz que implica cambio de sexo',
  'F4. Cambio de identidad o vestido/atuendo que implica rebajamiento social',
  'F5. Mujer tapada',
  'F6. Desaparecer / esconderse (para engañar a alguien)',
  'F7. Engaños y mentiras deliberadas (verbales y no verbales) de los protagonistas',
  'F8. Engaños y mentiras deliberadas (verbales y no verbales) de los criados',
  'F9. Disfraz o embozo',
  'F10. Cambio de identidad para conocer a la prometida de incógnito',

  // G. EQUÍVOCOS
  'G1. Vestidos o atuendos impropios que generan equívoco',
  'G2. Equívocos generados por una dama tapada',
  'G3. Equívocos verbales (incluidos engañar con la verdad, alusiones, hablar cubierto)',
  'G4. Equívocos espaciales (nacidos de engaños o escondites)',
  'G5. Equívocos generados por la oscuridad',

  // H. HONOR (AMENAZAS Y DEFENSAS)
  'H1. Recuperación del honor de una mujer soltera',
  'H2. Dudas del marido o del enamorado acerca del honor de la mujer',
  'H3. Padre o hermano que vela por el honor de su-s hija-s o hermana-s',
  'H4. Venganzas de honor',
  'H5. Poderoso que amenaza el honor de una mujer',
  'H6. Las leyes del honor (escrúpulos, ley del duelo, comportamiento honroso)',
  'H7. Dudas acerca del propio honor / de la propia identidad',

  // I. ACTOS Y SITUACIONES DE VIOLENCIA O PREVARICACIÓN
  'I1. Encarcelamiento / Destierro',
  'I2. Asesinato',
  'I3. Duelo que termina en muerte de uno de los contrincantes',
  'I4. Condena a muerte / Otros actos de violencia ordenados por el poder',
  'I5. Violencia sexual o intento de violencia sexual',
  'I6. La guerra y sus consecuencias nefastas',
  'I7. Luchas y enfrentamientos violentos',
  'I8. Traiciones violentas de varios tipos',
  'I9. Rapto',
  'I10. Escenas sangrientas (cabezas cortadas, muertes en el tablado, etc.)',
  'I11. Desaires violentos de todo tipo',
  'I12. Violencias y otros actos generados por la ambición',
  'I13. Suicidio',
  'I14. Manifestaciones de soberbia',

  // J. HAZAÑAS Y ACTOS DE VALOR
  'J1. Duelos y desafíos',
  'J2. Torneos y justas',
  'J3. Campañas bélicas',
  'J4. Victoria en la guerra',
  'J5. Rescatar a alguien de un peligro grave',
  'J6. Desestimar avisos / subestimar peligros',
  'J7. Fuerza de la sangre (la sangre noble impulsa a acciones nobles)',
  'J8. "Vencerse a sí mismo"',
  'J9. Actos de valor',

  // K. HADO, FORTUNA Y ALBEDRÍO HUMANO
  'K1. Caída en desgracia de un privado',
  'K2. Caída en desgracia de un poderoso',
  'K3. Horóscopos y vaticinios',
  'K4. Presagios infaustos',
  'K5. Vencer a la Fortuna / al Hado',
  'K6. Acciones o decisiones encaminadas a evitar consecuencias negativas u horóscopos nefastos',
  'K7. Llanto o duelo ante una muerte o desgracia',
  'K8. Augurios o revelaciones involuntarios',
  'K9. Reflexiones sobre la inconstancia de la Fortuna',

  // L. SITUACIONES O MOTIVOS NUCLEARES CON FUERTE CARGA SIMBÓLICA O SEMÁNTICA
  'L1. Naufragios',
  'L2. Incendios',
  'L3. Caída del caballo / Caballo desbocado',
  'L4. Cárcel / Pugna por salir de la cárcel',
  'L5. Música y sus efectos',
  'L6. El laberinto',
  'L7. El doble',
  'L8. Reconocimiento o anagnórisis',
  'L81. Reconocimiento gracias a un objeto',
  'L82. Reconocimiento gracias a un relato',
  "L83. Reconocimiento gracias a la 'fuerza de la sangre'",
  'L84. Reconocimiento interrumpido o aplazado',
  'L9. La caza',
  'L10. El sueño',
  'L11. Enigmas, adivinanzas, lenguaje cifrado',
  'L12. Academias poéticas',
  'L13. Sátiras',
  'L14. El espejo',
  'L15. Metateatro y escenas metateatrales',
  'L16. El pasaje secreto',
  'L17. Bailes y danzas',
  'L18. Discusiones, debates, razonamientos',
  'L19. Relaciones',

  // M. PERSONAJES Y ELEMENTOS CÓMICOS
  'M1. Bromas del gracioso o de la criada',
  'M2. Miedo cómico',
  'M3. Elementos escatológicos y corporales bajos',
  'M4. Ignorancia cómica',
  'M5. Cuentecillos cómicos',
  'M6. Peleas cómicas',
  'M7. Petición de dinero, ayuda o regalos',
  'M8. Quejas del gracioso o de la criada',
];
